import { type NotificationRuleItem } from '@utazzo/shared';
import {
  UzBadge,
  UzButton,
  type UzColumn,
  UzConfirmDialog,
  UzDrawer,
  UzInput,
  UzNumberInput,
  UzPagination,
  UzSelect,
  UzSwitch,
  UzTable,
  UzTextarea,
} from '@utazzo/ui';
import { Pencil, Play, Plus, Trash2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import {
  useCreateNotificationRule,
  useDeleteNotificationRule,
  useDryRunNotificationRule,
  useNotificationAudiences,
  useNotificationChannels,
  useNotificationEvents,
  useNotificationRules,
  useNotificationTemplates,
  useUpdateNotificationRule,
} from '../../use-notifications';

export const NotificationRulesTab = (): React.JSX.Element => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [eventCode, setEventCode] = useState<string>('');
  const [channelCode, setChannelCode] = useState<string>('');
  const [editing, setEditing] = useState<NotificationRuleItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<NotificationRuleItem | null>(null);
  const [dryRunning, setDryRunning] = useState<NotificationRuleItem | null>(null);

  const eventsQuery = useNotificationEvents({ page: 1, pageSize: 200, sortDir: 'asc' });
  const channelsQuery = useNotificationChannels();
  const audiencesQuery = useNotificationAudiences({ page: 1, pageSize: 200, sortDir: 'asc' });
  const templatesQuery = useNotificationTemplates({ page: 1, pageSize: 200, sortDir: 'asc' });

  const eventOptions = useMemo(
    () => (eventsQuery.data?.data ?? []).map((e) => ({ value: e.code, label: e.code })),
    [eventsQuery.data],
  );
  const channelOptions = useMemo(
    () => (channelsQuery.data?.data ?? []).map((c) => ({ value: c.code, label: c.code })),
    [channelsQuery.data],
  );
  const audienceOptions = useMemo(
    () =>
      (audiencesQuery.data?.data ?? []).map((a) => ({
        value: a.id,
        label: `${a.code} - ${a.name}`,
      })),
    [audiencesQuery.data],
  );
  const templateOptions = useMemo(
    () =>
      (templatesQuery.data?.data ?? []).map((t) => ({
        value: t.id,
        label: `${t.eventCode} / ${t.channelCode} - ${t.name}`,
      })),
    [templatesQuery.data],
  );

  const query = {
    page,
    pageSize,
    sortDir: 'asc' as const,
    sortBy: 'eventCode',
    ...(eventCode ? { eventCode } : {}),
    ...(channelCode ? { channelCode } : {}),
  };
  const rulesQuery = useNotificationRules(query);
  const updateRule = useUpdateNotificationRule();
  const deleteRule = useDeleteNotificationRule();

  const data = rulesQuery.data?.data ?? [];

  const columns: ReadonlyArray<UzColumn<NotificationRuleItem>> = [
    {
      id: 'code',
      header: 'Code',
      cell: (row) => <span className="font-mono text-xs text-zinc-800">{row.code}</span>,
      width: '180px',
    },
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-zinc-900">{row.name}</span>
          {row.description && <span className="text-xs text-zinc-500">{row.description}</span>}
        </div>
      ),
    },
    {
      id: 'event',
      header: 'Event',
      cell: (row) => <span className="font-mono text-xs text-zinc-700">{row.eventCode}</span>,
      width: '200px',
    },
    {
      id: 'channel',
      header: 'Channel',
      cell: (row) => (
        <UzBadge tone="secondary" pill>
          {row.channelCode}
        </UzBadge>
      ),
      width: '110px',
    },
    {
      id: 'priority',
      header: 'P',
      cell: (row) => <span className="font-mono text-xs text-zinc-700">{row.priority}</span>,
      width: '50px',
      align: 'right',
    },
    {
      id: 'active',
      header: 'Active',
      cell: (row) => (
        <UzSwitch
          checked={row.isActive}
          disabled={updateRule.isPending}
          onCheckedChange={(checked) =>
            updateRule.mutate({ id: row.id, payload: { isActive: checked } })
          }
        />
      ),
      width: '80px',
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <UzButton variant="ghost" size="sm" onClick={() => setDryRunning(row)} title="Dry-run">
            <Play className="h-4 w-4" />
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => setEditing(row)} title="Edit">
            <Pencil className="h-4 w-4" />
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => setDeleting(row)} title="Delete">
            <Trash2 className="h-4 w-4" />
          </UzButton>
        </div>
      ),
      width: '140px',
      align: 'right',
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <UzSelect
          label="Event"
          value={eventCode}
          onValueChange={(v: string) => {
            setPage(1);
            setEventCode(v);
          }}
          options={[{ value: '', label: 'All events' }, ...eventOptions]}
          containerClassName="w-64"
        />
        <UzSelect
          label="Channel"
          value={channelCode}
          onValueChange={(v: string) => {
            setPage(1);
            setChannelCode(v);
          }}
          options={[{ value: '', label: 'All channels' }, ...channelOptions]}
          containerClassName="w-44"
        />
        <div className="ml-auto">
          <UzButton size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New rule
          </UzButton>
        </div>
      </div>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={rulesQuery.isLoading}
      />
      <UzPagination
        page={page}
        pageSize={pageSize}
        totalItems={rulesQuery.data?.pagination.totalItems ?? 0}
        onPageChange={setPage}
        pageSizeOptions={[20, 50, 100]}
        onPageSizeChange={(s) => {
          setPage(1);
          setPageSize(s);
        }}
      />

      {creating && (
        <RuleFormDrawer
          mode="create"
          eventOptions={eventOptions}
          channelOptions={channelOptions}
          audienceOptions={audienceOptions}
          templateOptions={templateOptions}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && (
        <RuleFormDrawer
          mode="edit"
          initial={editing}
          eventOptions={eventOptions}
          channelOptions={channelOptions}
          audienceOptions={audienceOptions}
          templateOptions={templateOptions}
          onClose={() => setEditing(null)}
        />
      )}
      {dryRunning && <DryRunDrawer rule={dryRunning} onClose={() => setDryRunning(null)} />}

      <UzConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete rule?"
        description={`"${deleting?.name ?? ''}" stops firing immediately. Existing in-flight notifications are unaffected.`}
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={() => {
          if (deleting) deleteRule.mutate(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
};

interface RuleFormDrawerProps {
  mode: 'create' | 'edit';
  initial?: NotificationRuleItem;
  eventOptions: ReadonlyArray<{ value: string; label: string }>;
  channelOptions: ReadonlyArray<{ value: string; label: string }>;
  audienceOptions: ReadonlyArray<{ value: string; label: string }>;
  templateOptions: ReadonlyArray<{ value: string; label: string }>;
  onClose: () => void;
}

const RuleFormDrawer = ({
  mode,
  initial,
  eventOptions,
  channelOptions,
  audienceOptions,
  templateOptions,
  onClose,
}: RuleFormDrawerProps): React.JSX.Element => {
  const create = useCreateNotificationRule();
  const update = useUpdateNotificationRule();
  const pending = create.isPending || update.isPending;

  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [eventCode, setEventCode] = useState(initial?.eventCode ?? '');
  const [channelCode, setChannelCode] = useState(initial?.channelCode ?? '');
  const [audienceId, setAudienceId] = useState(initial?.audienceId ?? '');
  const [templateId, setTemplateId] = useState(initial?.templateId ?? '');
  const [conditionsJson, setConditionsJson] = useState(
    initial?.conditions ? JSON.stringify(initial.conditions, null, 2) : '',
  );
  const [throttleSeconds, setThrottleSeconds] = useState<number | null>(
    initial?.throttleSeconds ?? null,
  );
  const [quietHoursStart, setQuietHoursStart] = useState<number | null>(
    initial?.quietHoursStart ?? null,
  );
  const [quietHoursEnd, setQuietHoursEnd] = useState<number | null>(initial?.quietHoursEnd ?? null);
  const [priority, setPriority] = useState<number | null>(initial?.priority ?? 100);

  const parseConditions = (): unknown => {
    if (!conditionsJson.trim()) return null;
    try {
      return JSON.parse(conditionsJson);
    } catch {
      return null;
    }
  };

  const submit = (): void => {
    const conditions = parseConditions();
    if (mode === 'create') {
      create.mutate(
        {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          eventCode: eventCode.trim(),
          channelCode: channelCode.trim(),
          audienceId,
          templateId: templateId || null,
          conditions: conditions ?? null,
          throttleSeconds: throttleSeconds ?? null,
          quietHoursStart: quietHoursStart ?? null,
          quietHoursEnd: quietHoursEnd ?? null,
          priority: priority ?? 100,
        },
        { onSuccess: onClose },
      );
    } else if (initial) {
      update.mutate(
        {
          id: initial.id,
          payload: {
            name: name.trim(),
            description: description.trim() || undefined,
            audienceId,
            templateId: templateId || null,
            conditions: conditions ?? null,
            throttleSeconds: throttleSeconds ?? null,
            quietHoursStart: quietHoursStart ?? null,
            quietHoursEnd: quietHoursEnd ?? null,
            priority: priority ?? 100,
          },
        },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <UzDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={mode === 'create' ? 'New rule' : `Edit ${initial?.name ?? ''}`}
      description="Wire an event to a channel + template + audience."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton
            onClick={submit}
            disabled={
              pending || !code.trim() || !name.trim() || !eventCode || !channelCode || !audienceId
            }
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </UzButton>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <UzInput
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={pending || mode === 'edit'}
          required
        />
        <UzInput
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={pending}
          required
        />
        <UzTextarea
          label="Description"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={pending}
        />
        <UzSelect
          label="Event"
          value={eventCode}
          onValueChange={(v: string) => setEventCode(v)}
          options={eventOptions}
          disabled={pending || mode === 'edit'}
        />
        <UzSelect
          label="Channel"
          value={channelCode}
          onValueChange={(v: string) => setChannelCode(v)}
          options={channelOptions}
          disabled={pending || mode === 'edit'}
        />
        <UzSelect
          label="Audience"
          value={audienceId}
          onValueChange={(v: string) => setAudienceId(v)}
          options={audienceOptions}
          disabled={pending}
        />
        <UzSelect
          label="Template (optional)"
          value={templateId ?? ''}
          onValueChange={(v: string) => setTemplateId(v)}
          options={[{ value: '', label: 'No template (channel default)' }, ...templateOptions]}
          disabled={pending}
        />
        <UzTextarea
          label="Conditions (JSON, optional)"
          rows={4}
          value={conditionsJson}
          onChange={(e) => setConditionsJson(e.target.value)}
          disabled={pending}
          hint='Tiny DSL: {"field":"priority","op":"eq","value":"URGENT"} or {"and":[...]} etc.'
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <UzNumberInput
            label="Throttle (sec)"
            value={throttleSeconds ?? ''}
            onValueChange={setThrottleSeconds}
            min={0}
            max={86_400 * 30}
            step={60}
            precision={0}
            disabled={pending}
          />
          <UzNumberInput
            label="Priority"
            value={priority ?? ''}
            onValueChange={setPriority}
            min={0}
            max={1000}
            step={10}
            precision={0}
            disabled={pending}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <UzNumberInput
            label="Quiet hours start (0-23)"
            value={quietHoursStart ?? ''}
            onValueChange={setQuietHoursStart}
            min={0}
            max={23}
            step={1}
            precision={0}
            disabled={pending}
          />
          <UzNumberInput
            label="Quiet hours end (0-23)"
            value={quietHoursEnd ?? ''}
            onValueChange={setQuietHoursEnd}
            min={0}
            max={23}
            step={1}
            precision={0}
            disabled={pending}
          />
        </div>
      </div>
    </UzDrawer>
  );
};

const DryRunDrawer = ({
  rule,
  onClose,
}: {
  rule: NotificationRuleItem;
  onClose: () => void;
}): React.JSX.Element => {
  const dryRun = useDryRunNotificationRule();
  const [payloadJson, setPayloadJson] = useState('{}');

  const run = (): void => {
    let payload: Record<string, unknown> = {};
    try {
      payload = JSON.parse(payloadJson) as Record<string, unknown>;
    } catch {
      payload = {};
    }
    dryRun.mutate({ id: rule.id, payload: { payload } });
  };

  return (
    <UzDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Dry-run: ${rule.name}`}
      description="Resolves recipients without sending. Useful for validating an audience + condition combo."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose}>
            Close
          </UzButton>
          <UzButton onClick={run} disabled={dryRun.isPending}>
            <Play className="mr-1.5 h-4 w-4" />
            Run
          </UzButton>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <UzTextarea
          label="Sample event payload (JSON)"
          rows={8}
          value={payloadJson}
          onChange={(e) => setPayloadJson(e.target.value)}
          disabled={dryRun.isPending}
        />
        {dryRun.data && (
          <div className="rounded-md border border-zinc-200 p-3">
            <div className="flex items-center gap-2">
              <UzBadge tone={dryRun.data.matched ? 'success' : 'secondary'} pill>
                {dryRun.data.matched ? 'Matched' : 'Skipped'}
              </UzBadge>
              <span className="text-sm text-zinc-700">
                {dryRun.data.recipientCount} recipient(s)
              </span>
            </div>
            {dryRun.data.reason && (
              <p className="mt-2 text-xs text-zinc-500">{dryRun.data.reason}</p>
            )}
            {dryRun.data.recipientUserIds.length > 0 && (
              <pre className="mt-2 max-h-48 overflow-auto rounded bg-zinc-50 p-2 text-[11px] text-zinc-700">
                {dryRun.data.recipientUserIds.join('\n')}
              </pre>
            )}
          </div>
        )}
      </div>
    </UzDrawer>
  );
};
